import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { leadershipData } from '../data/Leadership';
import { useTheme } from '../contexts/ThemeContext';

function Leadership() {
  const { isDark } = useTheme();
  const [activeIndex, setActiveIndex] = useState(0);

  // Auto rotate images every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % leadershipData.events.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6 p-2 sm:p-4 h-full max-h-[calc(100vh-80px)]  overflow-hidden">
      {/* Left Column - Content */}
      <div className="w-full lg:w-1/2 space-y-4 sm:space-y-6 lg:pr-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className={`text-2xl sm:text-3xl lg:text-4xl font-heading font-bold mb-2 ${
            isDark ? 'text-dark-text' : 'text-light-text'
          }`}>
            {leadershipData.title}
          </h1>
          <h2 className={`text-lg sm:text-xl font-display mb-2 sm:mb-4 ${
            isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'
          }`}>
            {leadershipData.role}
          </h2>
          <p className={`text-sm sm:text-base font-body ${
            isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'
          }`}>
            {leadershipData.description}
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          {leadershipData.stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`p-2 sm:p-4 rounded-xl ${
                isDark ? 'bg-dark-secondary' : 'bg-light-secondary'
              }`}
            >
              <div className={`text-lg sm:text-2xl font-bold mb-1 ${
                isDark ? 'text-dark-text' : 'text-light-text'
              }`}>
                {stat.value}
              </div>
              <div className={`text-xs sm:text-sm ${
                isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'
              }`}>
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          <a 
            href="https://gdg.community.dev/gdg-on-campus-pune-district-education-associations-college-of-engineering-pune-india/" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-lg font-display text-sm sm:text-base"
            >
              Join Community
            </motion.button>
          </a>
        </div>
      </div>

      {/* Right Column - Bento Grid */}
      <div className="w-full lg:w-1/2 mt-4 lg:mt-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
          {leadershipData.events.map((event, index) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className={`relative rounded-2xl overflow-hidden ${
                event.size === 'large' ? 'sm:col-span-2 sm:row-span-2' :
                event.size === 'medium' ? 'sm:col-span-2' :
                event.size === 'wide' ? 'sm:col-span-2' : ''
              }`}
              whileHover={{ scale: 1.03 }}
            >
              <img
                src={event.imageUrl}
                alt={event.title}
                className={`w-full object-cover ${
                  event.size === 'wide' ? 'h-24 sm:h-32' : 'h-32 sm:h-40'
                }`}
                style={{
                  objectPosition: event.id === 1 ? 'center 65%' : // Gen AI Study Jam
                              event.id === 2 ? 'center 40%' : // Tech Winter Break
                              event.id === 3 ? 'center 35%' : // Build On Aptos
                              event.id === 4 ? 'center 45%' : // Solution Challenge
                              event.id === 5 ? 'center 30%' : // Team
                              'center center' // Default
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent">
                <div className="absolute bottom-2 left-2 sm:left-3 text-white"> {/* Adjusted padding */}
                  <h3 className="font-display text-sm sm:text-base">{event.title}</h3> {/* Reduced font size */}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Leadership;